import { Link } from 'react-router-dom'
import styles from './LegalDocumentPage.module.css'

const CONTACT_EMAIL =
  (import.meta.env.VITE_SALES_CONTACT_EMAIL as string | undefined)?.trim() || 'comercial@gobooking.pt'
const PRIVACY_EMAIL = 'contacto@gobooking.pt'
const UPDATED = '7 de Setembro de 2026'

type LegalKind = 'privacidade' | 'termos'

const TITLES: Record<LegalKind, string> = {
  privacidade: 'Política de Privacidade',
  termos: 'Termos de Utilização',
}

function PrivacyBody() {
  return (
    <>
      <p>
        Esta Política de Privacidade descreve como a plataforma de agendamentos{' '}
        <strong>GoBooking</strong>, operada pela <strong>Sol Online</strong> («nós», «plataforma»),
        trata os dados pessoais dos utilizadores das aplicações web e mobile (incluindo apps white-label
        publicadas nas lojas).
      </p>
      <p>
        Cada negócio (tenant) utiliza o seu próprio espaço (por exemplo{' '}
        <code>gobooking.pt/e/sua-empresa</code>). Os dados de clientes e agendamentos desse negócio
        ficam associados a essa conta.
      </p>

      <h2>1. Responsável pelo tratamento</h2>
      <p>
        Para questões gerais da plataforma: <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a> (ou{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>).
      </p>
      <p>
        Relativamente aos dados dos clientes finais de um negócio concreto (marcações, contacto), o responsável
        pelo tratamento é, em regra, esse negócio (o titular da conta na plataforma), que decide para que fins
        utiliza a agenda. A Sol Online (GoBooking) actua como prestadora técnica da plataforma.
      </p>

      <h2>2. Dados que podemos tratar</h2>
      <ul>
        <li>
          <strong>Conta de cliente:</strong> nome, e-mail, telefone, código de acesso / autenticação, fotografia de
          perfil (se fornecida).
        </li>
        <li>
          <strong>Agendamentos:</strong> data, hora, unidade, profissional, serviço, estado (agendado, realizado,
          cancelado) e observações eventualmente registadas.
        </li>
        <li>
          <strong>Conta de profissional / administrador:</strong> nome, e-mail, credenciais, preferências e, se
          activado, ligação à conta Google Calendar (tokens de acesso guardados de forma segura no servidor).
        </li>
        <li>
          <strong>Conteúdo do negócio:</strong> textos, imagens, galeria, contactos e configurações publicados pelo
          administrador.
        </li>
        <li>
          <strong>Dados técnicos:</strong> registos de sessão (cookies ou tokens), endereço IP e dados de
          diagnóstico necessários ao funcionamento e segurança do serviço.
        </li>
      </ul>

      <h2>3. Finalidades</h2>
      <ul>
        <li>Permitir registo, login e gestão de marcações.</li>
        <li>Enviar confirmações, lembretes e códigos de acesso por e-mail (quando o SMTP estiver configurado).</li>
        <li>
          Sincronizar agendamentos com o Google Calendar do profissional, quando este autorizar explicitamente a
          ligação (incluindo convites por e-mail ao cliente, quando aplicável).
        </li>
        <li>Operar painéis de administração e de profissional.</li>
        <li>Garantir segurança, prevenção de abuso e cumprimento de obrigações legais.</li>
        <li>Melhorar a estabilidade e o suporte técnico da plataforma.</li>
      </ul>

      <h2>4. Bases legais (RGPD)</h2>
      <p>Consoante o caso, o tratamento assenta em:</p>
      <ul>
        <li>execução de contrato / prestação do serviço de agendamento;</li>
        <li>consentimento (por exemplo, ligação ao Google Calendar);</li>
        <li>interesses legítimos (segurança, melhoria do serviço), sem prevalecer sobre os seus direitos;</li>
        <li>obrigações legais, quando aplicável.</li>
      </ul>

      <h2>5. Partilha de dados</h2>
      <p>Não vendemos dados pessoais. Podemos partilhar dados apenas com:</p>
      <ul>
        <li>o negócio (tenant) junto do qual efectuou a marcação;</li>
        <li>
          prestadores técnicos essenciais (alojamento, e-mail transacional, Google — apenas se o profissional
          ligar o Calendar);
        </li>
        <li>autoridades, quando a lei o exigir.</li>
      </ul>
      <p>
        A utilização do Google Calendar está sujeita também às políticas da Google. Pode revogar o acesso nas
        definições da sua conta Google e/ou desligar a integração no portal do profissional.
      </p>

      <h2>6. Conservação</h2>
      <p>
        Conservamos os dados enquanto a conta ou a relação de agendamento forem necessárias à prestação do
        serviço e pelos prazos adicionais exigidos por lei ou para defesa de direitos. Pode solicitar a
        eliminação nos termos legais aplicáveis.
      </p>

      <h2>7. Segurança</h2>
      <p>
        Aplicamos medidas técnicas e organizativas adequadas (acesso autenticado, isolamento por tenant,
        comunicação HTTPS em produção). Nenhum sistema é 100% isento de risco.
      </p>

      <h2>8. Os seus direitos</h2>
      <p>
        Nos termos do RGPD, pode solicitar acesso, rectificação, apagamento, limitação, portabilidade e oposição,
        bem como retirar o consentimento quando o tratamento se baseie nele. Pode também apresentar reclamação
        junto da autoridade de controlo competente (em Portugal, a CNPD).
      </p>
      <p>
        Para pedir a eliminação da conta, use a página{' '}
        <Link to="/eliminar-conta">Eliminar conta</Link> ou contacte{' '}
        <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>. Para dados de um negócio concreto, contacte
        também esse negócio.
      </p>

      <h2>9. Menores</h2>
      <p>
        O serviço não se destina a crianças sem supervisão de um adulto responsável. Se tomar conhecimento de
        dados indevidos de menores, contacte-nos para os analisarmos.
      </p>

      <h2>10. Alterações</h2>
      <p>
        Podemos actualizar esta política. A data de actualização figura no topo da página. O uso continuado do
        serviço após alterações relevantes implica conhecimento da versão publicada.
      </p>

      <h2>11. Contacto</h2>
      <p>
        Privacidade / suporte plataforma: <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>
        <br />
        Comercial: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
      </p>
    </>
  )
}

function TermsBody() {
  return (
    <>
      <p>
        Estes Termos regulam a utilização da plataforma de agendamentos <strong>GoBooking</strong>,
        da <strong>Sol Online</strong>,
        incluindo o site <code>gobooking.pt</code> e as aplicações mobile white-label publicadas nas lojas em
        nome dos negócios clientes.
      </p>

      <h2>1. Aceitação</h2>
      <p>
        Ao aceder ou utilizar a plataforma (como cliente, profissional, administrador de um negócio ou visitante),
        aceita estes Termos e a{' '}
        <Link to="/privacidade">Política de Privacidade</Link>. Se não concordar, não utilize o serviço.
      </p>

      <h2>2. Descrição do serviço</h2>
      <p>
        A plataforma permite a negócios criar um espaço próprio de marcações (unidades, profissionais, horários,
        conteúdos) e aos utilizadores finais agendar, consultar, remarcar ou cancelar serviços, conforme as
        regras definidas por cada negócio.
      </p>

      <h2>3. Contas e responsabilidades</h2>
      <ul>
        <li>
          <strong>Negócio (tenant):</strong> é responsável pelos conteúdos, preços, disponibilidade, cumprimento
          das marcações e pela relação comercial com os seus clientes.
        </li>
        <li>
          <strong>Profissional / administrador:</strong> deve manter credenciais seguras e utilizar a conta apenas
          para fins legítimos do negócio.
        </li>
        <li>
          <strong>Cliente:</strong> deve fornecer dados correctos e respeitar as políticas de cancelamento do
          negócio.
        </li>
      </ul>

      <h2>4. Utilização aceitável</h2>
      <p>É proibido, entre outros:</p>
      <ul>
        <li>utilizar o serviço de forma ilegal ou abusiva;</li>
        <li>tentar aceder a dados de outros tenants ou contornar autenticação;</li>
        <li>enviar spam ou conteúdo ilícito através das funcionalidades da plataforma;</li>
        <li>interferir com a disponibilidade ou segurança do sistema.</li>
      </ul>

      <h2>5. Integrações (ex.: Google Calendar)</h2>
      <p>
        Funcionalidades opcionais (como a sincronização com Google Calendar) dependem de autorização do
        utilizador e de serviços de terceiros. A GoBooking não controla a disponibilidade contínua desses
        serviços externos.
      </p>

      <h2>6. Propriedade intelectual</h2>
      <p>
        A plataforma, software e marca GoBooking pertencem aos respectivos titulares. Os conteúdos
        carregados por cada negócio (logo, textos, imagens) permanecem da responsabilidade e, em regra, da
        titularidade desse negócio.
      </p>

      <h2>7. Disponibilidade e exclusão de garantia</h2>
      <p>
        Procuramos manter o serviço disponível e seguro, mas não garantimos funcionamento ininterrupto ou isento
        de erros. Manutenções, falhas de rede ou de prestadores externos podem ocorrer.
      </p>

      <h2>8. Limitação de responsabilidade</h2>
      <p>
        Na medida permitida pela lei aplicável, a GoBooking não é responsável por disputas entre o negócio e o
        cliente final (incluindo não comparências, qualidade do serviço prestado presencialmente ou reembolsos),
        nem por danos indirectos decorrentes do uso da plataforma. A responsabilidade da plataforma limita-se, em
        regra, à prestação técnica do software.
      </p>

      <h2>9. Apps nas lojas</h2>
      <p>
        As aplicações publicadas na Google Play / App Store com a marca de um negócio são canais de acesso ao mesmo
        serviço. Continuam a aplicar-se estes Termos e a Política de Privacidade, sem prejuízo das regras das
        lojas.
      </p>

      <h2>10. Alterações e cessação</h2>
      <p>
        Podemos actualizar estes Termos; a data de actualização figura no topo. Podemos suspender ou encerrar
        contas que violem as regras. O negócio pode cessar a utilização da plataforma conforme o contrato
        comercial celebrado.
      </p>

      <h2>11. Lei aplicável</h2>
      <p>
        Salvo disposição imperativa em contrário, aplica-se a lei portuguesa. Eventuais litígios serão submetidos
        aos tribunais competentes em Portugal.
      </p>

      <h2>12. Contacto</h2>
      <p>
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
        <br />
        <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>
      </p>
    </>
  )
}

export function LegalDocumentPage({ kind }: { kind: LegalKind }) {
  return (
    <main className={styles.page}>
      <div className={styles.inner}>
        <p className={styles.brand}>
          <Link to="/">GoBooking</Link>
        </p>
        <h1 className={styles.title}>{TITLES[kind]}</h1>
        <p className={styles.updated}>Última actualização: {UPDATED}</p>
        <nav className={styles.nav} aria-label="Documentos legais">
          <Link to="/privacidade" className={kind === 'privacidade' ? styles.navActive : undefined}>
            Privacidade
          </Link>
          <Link to="/termos" className={kind === 'termos' ? styles.navActive : undefined}>
            Termos
          </Link>
          <Link to="/eliminar-conta">Eliminar conta</Link>
        </nav>
        <article className={styles.body}>{kind === 'privacidade' ? <PrivacyBody /> : <TermsBody />}</article>
        <p className={styles.note}>
          Este texto é informativo e destina-se a cumprir requisitos das lojas de aplicações e transparência com
          os utilizadores. Não substitui aconselhamento jurídico personalizado.
        </p>
        <p className={styles.back}>
          <Link to="/">← Voltar ao início</Link>
        </p>
      </div>
    </main>
  )
}

export function PrivacidadePage() {
  return <LegalDocumentPage kind="privacidade" />
}

export function TermosPage() {
  return <LegalDocumentPage kind="termos" />
}
